import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Wrapper around useTranslation that handles the cold-load case.
 *
 * Returns: { t, ready, LoadingGate, ...rest }
 *
 * Usage:
 *   const { t, ready, LoadingGate } = useReadyTranslation(['landing', 'common']);
 *   if (!ready) return <LoadingGate />;
 *   return <div>{t('key')}</div>;
 *
 * Why: with `useSuspense: false` in i18n.js, t(key, { returnObjects: true })
 * returns the key string as fallback during the namespace fetch window.
 * That breaks .map() consumers and shows literal keys in the UI for 100-200ms.
 * The ready flag gates rendering until namespaces have loaded.
 */
export function useReadyTranslation(namespaces) {
  const { t, ready, ...rest } = useTranslation(namespaces);

  const LoadingGate = () => (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div
        className="w-8 h-8 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin"
        data-testid="i18n-loading-spinner"
      />
    </div>
  );

  return { t, ready, LoadingGate, ...rest };
}
